import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, List, Card, Typography, Spin } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useGetStoryTitles, useUpdateStoryName } from '@coreStory/useCase';
import type { IStoryList, IStoryTitleSuggestion } from '@coreStory/type';
import styles from './index.module.scss';

const { Text } = Typography;

interface EditTitleModalProps {
  visible: boolean;
  onCancel: () => void;
  storyData: IStoryList;
  onSuccess?: () => void;
}

const EditTitleModal: React.FC<EditTitleModalProps> = ({
  visible,
  onCancel,
  storyData,
  onSuccess,
}) => {
  const [customTitle, setCustomTitle] = useState(storyData?.StoryName || '');
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [suggestions, setSuggestions] = useState<IStoryTitleSuggestion[]>([]);

  const {
    mutate: getStoryTitles,
    isLoading: isLoadingSuggestions,
    data: suggestionsData,
  } = useGetStoryTitles();

  const { mutate: updateStoryName, isLoading: isUpdating } =
    useUpdateStoryName();

  // Load suggestions when modal opens
  useEffect(() => {
    if (visible && storyData?.StoryID) {
      getStoryTitles(
        { story_id: storyData.StoryID },
        {
          onSuccess: (data) => {
            setSuggestions(data || []);
          },
        },
      );
    }
  }, [visible, storyData?.StoryID, getStoryTitles]);

  // Update suggestions when data is received
  useEffect(() => {
    if (suggestionsData) {
      setSuggestions(suggestionsData);
    }
  }, [suggestionsData]);

  const handleSave = () => {
    const titleToSave = selectedTitle || customTitle;

    if (!titleToSave.trim()) {
      return;
    }

    if (storyData?.StoryID) {
      updateStoryName(
        {
          story_id: storyData.StoryID,
          story_name: titleToSave.trim(),
        },
        {
          onSuccess: () => {
            onSuccess?.();
            onCancel();
          },
        },
      );
    }
  };

  const handleCancel = () => {
    setCustomTitle(storyData?.StoryName || '');
    setSelectedTitle('');
    setSuggestions([]);
    onCancel();
  };

  const handleSuggestionSelect = (title: string) => {
    setSelectedTitle(title);
    setCustomTitle(title);
  };

  const isLoading = isLoadingSuggestions || isUpdating;
  const finalTitle = selectedTitle || customTitle;

  return (
    <Modal
      title="Edit Story Title"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={isUpdating}
          onClick={handleSave}
          disabled={!finalTitle.trim()}>
          Save Title
        </Button>,
      ]}
      width={600}
      className={styles.editTitleModal}>
      <div className={styles.editTitleContent}>
        {/* Custom Title Input */}
        <div className={styles.customTitleSection}>
          <Text strong>Custom Title:</Text>
          <Input
            value={customTitle}
            onChange={(e) => {
              setCustomTitle(e.target.value);
              setSelectedTitle(''); // Clear selection when typing custom title
            }}
            placeholder="Enter your custom story title..."
            className={styles.titleInput}
            maxLength={100}
            disabled={isLoading}
          />
        </div>

        {/* Suggested Titles */}
        <div className={styles.suggestionsSection}>
          <Text strong>Suggested Titles:</Text>
          {isLoadingSuggestions ? (
            <div className={styles.loadingContainer}>
              <Spin size="large" />
              <Text className={styles.loadingText}>
                Generating title suggestions...
              </Text>
            </div>
          ) : suggestions.length > 0 ? (
            <List
              grid={{ gutter: 8, xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }}
              dataSource={suggestions}
              className={styles.suggestionsList}
              renderItem={(item) => (
                <List.Item>
                  <Card
                    hoverable
                    className={`${styles.suggestionCard} ${
                      selectedTitle === String(item.Title)
                        ? styles.selected
                        : ''
                    }`}
                    onClick={() => handleSuggestionSelect(String(item.Title))}
                    bodyStyle={{ padding: '12px 16px' }}>
                    <div className={styles.suggestionContent}>
                      <Text className={styles.suggestionTitle}>
                        {item.Title}
                      </Text>
                      {selectedTitle === String(item.Title) && (
                        <CheckOutlined className={styles.selectedIcon} />
                      )}
                    </div>
                  </Card>
                </List.Item>
              )}
            />
          ) : (
            !isLoadingSuggestions && (
              <div className={styles.noSuggestions}>
                <Text type="secondary">
                  No suggestions available at the moment.
                </Text>
              </div>
            )
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EditTitleModal;
